/**
 * People: everyone who registered, by group (Everyone, New this week, Watching now, Need a word, Went quiet, a tag)
 * or by a room's stage, as faces or a list that keeps loading as you scroll. `/people/:id` is one person.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { noteAboutOf } from "../../../shared/people-notes";
import type { PeopleFilters, PeopleRail, RegistrantSummary, RoomStage } from "../../../shared/types";
import { FirstRoomEmpty } from "../../components/FirstRoomEmpty";
import { InviteSheet, type InviteTarget, invitable, inviteTargetOf } from "../../components/InviteSheet";
import { useLayout } from "../../components/season/layout";
import { Button, IconButton } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/EmptyState";
import { Face, FaceStack, Ring } from "../../components/ui/Face";
import { Icon } from "../../components/ui/Icon";
import { Seg } from "../../components/ui/Seg";
import { useToast } from "../../components/ui/Toast";
import { api } from "../../lib/api";
import { useShownAgain } from "../../lib/inApp";
import { fmt } from "../../lib/when";
import { depthOf, GROUP_LABEL, lastLine, peopleWord, STAGE_NAME, usePeople, whereLine } from "./list";
import { PersonPage } from "./PersonPage";
import { SeasonPeople } from "./SeasonPeople";
import { SeasonPerson } from "./SeasonPerson";
import { AutomationSheet, GroupNoteSheet, NoteSheet } from "./sheets";
import { StagePill } from "./stage";
import "../../styles/people.css";

export function PeopleView() {
  const { id } = useParams();
  const layout = useLayout();
  if (layout === "computer") return id ? <PersonPage id={id} /> : <PeopleList />;
  if (layout === "side") return <SeasonPeople layout="side" openId={id} />;
  return id ? <SeasonPerson id={id} layout={layout} /> : <SeasonPeople layout={layout} />;
}

function PeopleList() {
  const navigate = useNavigate();
  const toast = useToast();
  const shownAgain = useShownAgain();
  const { key, data, failed, q, setQ, view, setView, limit, showMore, shown, rail, everyone, room, filters, title, n, groupAct, clearable, exportCsv } = usePeople();
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [note, setNote] = useState<RegistrantSummary | null>(null);
  const [groupNote, setGroupNote] = useState(false);
  const [autoFor, setAutoFor] = useState<string[] | null>(null);
  const [inviteTo, setInviteTo] = useState<InviteTarget[] | null>(null);

  // Why: a pick belongs to the list it was made in.
  useEffect(() => setPicked(new Set()), [key, shownAgain]);

  const flip = (pid: string) =>
    setPicked((s) => {
      const next = new Set(s);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  const pickedPeople = shown.filter((p) => picked.has(p.id));
  const openInvite = () => {
    api.webinars
      .list()
      .then((r) => {
        const choices = r.webinars.filter(invitable).map(inviteTargetOf);
        if (choices.length === 0) toast({ text: "Publish a room first, then invite people to it.", icon: "info" });
        else setInviteTo(choices);
      })
      .catch(() => toast({ text: "Your rooms didn't load. Try again.", icon: "info" }));
  };

  const nobodyAtAll = rail !== null && everyone === 0;

  return (
    <div className="page wide">
      <div className="ph">
        <div>
          <h1>People</h1>
          {rail ? <p>{`${fmt(everyone)} registered`}</p> : null}
        </div>
        {nobodyAtAll ? null : (
          <div className="row g8 wrap">
            <div className="ig psearch">
              <span className="fix">
                <Icon name="search" />
              </span>
              <input dir="auto" className="inp" placeholder="Find someone" aria-label="Find someone" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <Seg
              options={[
                { value: "faces", label: "Faces", icon: "grid" },
                { value: "list", label: "List", icon: "list" },
              ]}
              value={view}
              onChange={setView}
              label="Show people as"
            />
            <Button icon="download" onClick={exportCsv} disabled={!shown.length}>
              Export
            </Button>
          </div>
        )}
      </div>
      {nobodyAtAll ? (
        <section className="card">
          <FirstRoomEmpty place="people" />
        </section>
      ) : (
        <div className="plyt">
          {rail ? <Rail rail={rail} filters={filters} /> : <nav className="card segs" aria-busy="true" />}
          <div style={{ minWidth: 0 }}>
            <section className="card seghead">
              <div className="sp" style={{ minWidth: 0 }}>
                <div className="row g12 wrap">
                  <h2>{title}</h2>
                  <span className="pill">{`${fmt(n)} ${peopleWord(n)}`}</span>
                  {clearable ? (
                    <Link className="link" to="/people">
                      <Icon name="x" /> Clear
                    </Link>
                  ) : null}
                </div>
              </div>
              {groupAct && n > 0 ? (
                <Button variant="soft" icon="send" onClick={() => setGroupNote(true)}>
                  {groupAct.label}
                </Button>
              ) : null}
              <Button icon="zap" onClick={() => setAutoFor(shown.map((p) => p.id))} disabled={n === 0}>
                Add to automation
              </Button>
            </section>
            {failed && !data ? (
              <section className="card pad">
                <div className="row g12">
                  <span className="sp">People didn't load.</span>
                  <Button size="s" onClick={() => navigate(0)}>
                    Try again
                  </Button>
                </div>
              </section>
            ) : !data ? (
              <section className="card pad" aria-busy="true">
                <div className="small muted">Loading</div>
              </section>
            ) : n === 0 ? (
              <section className="card pad empty">
                <EmptyState icon="users" title={q ? "No one by that name here" : "No one here yet"} />
              </section>
            ) : view === "list" ? (
              <>
                <PeopleTable people={shown.slice(0, limit)} inRoom={!!room} picked={picked} onPick={flip} />
                {limit < n ? (
                  <div className="pmore">
                    <span>and {fmt(n - limit)} more</span>
                    <Button size="s" onClick={showMore}>
                      Show more
                    </Button>
                  </div>
                ) : null}
              </>
            ) : (
              <div className="pgrid">
                {shown.slice(0, limit).map((p) => (
                  <PersonCard key={p.id} p={p} onNote={() => setNote(p)} />
                ))}
                {limit < n ? <MoreCard next={shown.slice(limit, limit + 4)} more={n - limit} onMore={showMore} /> : null}
              </div>
            )}
            {picked.size > 0 ? (
              <div className="savebar">
                <FaceStack people={pickedPeople.slice(0, 5).map((p) => ({ name: p.name, photo: p.avatar_url }))} size={28} />
                <b>{`${fmt(picked.size)} picked`}</b>
                <span className="sp" />
                <Button size="s" icon="userplus" onClick={openInvite}>
                  Invite to a room
                </Button>
                <Button size="s" icon="zap" onClick={() => setAutoFor([...picked])}>
                  Add to automation
                </Button>
                <button type="button" className="btn s ghost" onClick={() => setPicked(new Set())}>
                  Clear
                </button>
              </div>
            ) : null}
          </div>
        </div>
      )}
      <NoteSheet person={note} about={noteAboutOf(note?.seg === "quiet" ? "quiet" : undefined)} pill={note ? <StagePill p={note} /> : null} onClose={() => setNote(null)} />
      {groupAct ? (
        <GroupNoteSheet open={groupNote} title={groupAct.label} label={title} about={groupAct.about} people={shown} onClose={() => setGroupNote(false)} />
      ) : null}
      <AutomationSheet open={autoFor !== null} ids={autoFor ?? []} onClose={() => setAutoFor(null)} />
      {inviteTo ? (
        <InviteSheet
          choices={inviteTo}
          picked={pickedPeople.map((p) => ({ email: p.email, name: p.name }))}
          onClose={() => setInviteTo(null)}
          onSent={() => {
            setInviteTo(null);
            setPicked(new Set());
          }}
          onToast={(m) => toast(m)}
        />
      ) : null}
    </div>
  );
}

// ── the rail ───────────────────────────────────────────────────────────
function Rail({ rail, filters }: { rail: PeopleRail; filters: PeopleFilters }) {
  const sel = filters.room ? "" : (filters.group ?? "all");
  const to = (group: string) => (group === "all" ? "/people" : `/people?group=${encodeURIComponent(group)}`);
  return (
    <nav className="card segs" aria-label="Who to show">
      <div className="rlbl">People</div>
      {rail.groups.map((g) => (
        <Link key={g.key} className={sel === g.key ? "segi on" : "segi"} to={to(g.key)} aria-current={sel === g.key ? "page" : undefined}>
          <FaceStack people={g.faces.map((f) => ({ name: f.name, photo: f.avatar_url }))} size={22} />
          <span className="lbl2">{GROUP_LABEL[g.key]}</span>
          <span className="n">{fmt(g.count)}</span>
        </Link>
      ))}
      {rail.tags.length ? (
        <>
          <div className="rlbl">Tags</div>
          <div className="tagpick">
            {rail.tags.map((t) => (
              <Link key={t.tag} className={sel === `tag:${t.tag}` ? "tchip on" : "tchip"} to={to(`tag:${t.tag}`)}>
                {t.tag} <span>{fmt(t.count)}</span>
              </Link>
            ))}
          </div>
        </>
      ) : null}
      {rail.rooms.length ? (
        <>
          <div className="rlbl">By room</div>
          <div className="wpick">
            {rail.rooms.map((r) => {
              const open = filters.room === r.id;
              return (
                <div key={r.id} className={open ? "wpk-card open" : "wpk-card"}>
                  <Link className="wpk-head" to={open ? "/people" : `/people?room=${encodeURIComponent(r.id)}&stage=registered`} aria-expanded={open}>
                    <span className="wpk-thumb">{r.poster_url ? <img src={r.poster_url} alt="" /> : <Icon name="play" />}</span>
                    <span className="sp">
                      <b dir="auto">{r.title}</b>
                      <span>{`${r.kind} · ${fmt(r.stages.registered)} registered`}</span>
                    </span>
                    <Icon name={open ? "down" : "chev"} />
                  </Link>
                  {open ? <RoomStages room={r} sel={filters.stage ?? "registered"} range={filters.range} /> : null}
                </div>
              );
            })}
          </div>
        </>
      ) : null}
    </nav>
  );
}

function RoomStages({ room, sel, range }: { room: PeopleRail["rooms"][number]; sel: RoomStage; range: PeopleFilters["range"] }) {
  const reg = Math.max(1, room.stages.registered);
  const href = (st: RoomStage) => `/people?room=${encodeURIComponent(room.id)}&stage=${st}${range ? `&range=${range}` : ""}`;
  const step = (st: RoomStage) => (
    <Link className={sel === st ? "wf on" : "wf"} to={href(st)} aria-current={sel === st ? "page" : undefined}>
      <span className="wf-l">{STAGE_NAME[st]}</span>
      <span className="wf-n">{fmt(room.stages[st])}</span>
      <span className="wf-bar">
        <i style={{ width: `${Math.max(2, Math.round((room.stages[st] / reg) * 100))}%` }} />
      </span>
    </Link>
  );
  return (
    <div className="wfun">
      {step("registered")}
      {step("came")}
      {step("reached")}
      {step("clicked")}
      <Link className={sel === "nosign" ? "wf-sub on" : "wf-sub"} to={href("nosign")}>
        {`${fmt(room.stages.nosign)} of them not customers yet`} <Icon name="chev" />
      </Link>
      {step("signed")}
      <div className="wf-sep" />
      <Link className={sel === "missed" ? "wf wf-miss on" : "wf wf-miss"} to={href("missed")}>
        <span className="wf-l">Missed it</span>
        <span className="wf-n">{fmt(room.stages.missed)}</span>
        <span className="wf-note">Registered, never came</span>
      </Link>
    </div>
  );
}

// ── a person, as a face or a row ─────────────────────────────────────────
function PersonCard({ p, onNote }: { p: RegistrantSummary; onNote: () => void }) {
  return (
    <Link className="card pcard" to={`/people/${p.id}`} data-opens={`person:${p.id}`}>
      <IconButton
        icon="send"
        label={`Send ${p.name.split(" ")[0]} a note`}
        small
        className="qa"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onNote();
        }}
      />
      <Ring name={p.name} photo={p.avatar_url} size={72} percent={depthOf(p)} live={!!p.watching} />
      <div>
        <div className="nm"><bdi>{p.name}</bdi></div>
      </div>
      <StagePill p={p} />
      <div className="ds" style={{ marginTop: -4 }}>
        {whereLine(p)}
      </div>
      {p.tags.length ? (
        <div className="tags">
          {p.tags.map((t) => (
            <span key={t} className="tag">
              {t}
            </span>
          ))}
        </div>
      ) : null}
      <div className="foot">
        <span>{p.source ?? ""}</span>
        <span>{lastLine(p)}</span>
      </div>
    </Link>
  );
}

/** The grid's last card: the next few faces, how many are left, and the button that draws the next page. */
function MoreCard({ next, more, onMore }: { next: RegistrantSummary[]; more: number; onMore: () => void }) {
  return (
    <article className="card pcard morecard">
      <FaceStack people={next.map((p) => ({ name: p.name, photo: p.avatar_url }))} size={40} />
      <div>
        <div className="nm">and {fmt(more)} more</div>
      </div>
      <Button size="s" onClick={onMore}>
        Show more
      </Button>
    </article>
  );
}

function PeopleTable({
  people,
  inRoom,
  picked,
  onPick,
}: {
  people: RegistrantSummary[];
  inRoom: boolean;
  picked: ReadonlySet<string>;
  onPick: (id: string) => void;
}) {
  const navigate = useNavigate();
  return (
    <section className="card ptwrap" style={{ overflow: "hidden" }}>
      <table className="ptable">
        <thead>
          <tr>
            <th style={{ width: 36 }} aria-label="Pick" />
            <th>Person</th>
            <th>Room</th>
            <th>Stage there</th>
            <th>Watch depth</th>
            <th>Tags</th>
            <th>Last activity</th>
          </tr>
        </thead>
        <tbody>
          {people.map((p) => (
            <tr key={p.id} onClick={() => navigate(`/people/${p.id}`)} data-opens={`person:${p.id}`}>
              <td>
                <button
                  type="button"
                  className={picked.has(p.id) ? "check on" : "check"}
                  aria-label={picked.has(p.id) ? `Unpick ${p.name}` : `Pick ${p.name}`}
                  aria-pressed={picked.has(p.id)}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPick(p.id);
                  }}
                >
                  {picked.has(p.id) ? <Icon name="check" /> : null}
                </button>
              </td>
              <td>
                <div className="who">
                  <Face name={p.name} photo={p.avatar_url} size={34} />
                  <div style={{ minWidth: 0 }}>
                    <div className="nm"><bdi>{p.name}</bdi></div>
                    <div className="ds">{p.email}</div>
                  </div>
                </div>
              </td>
              <td>
                <div className="b6">
                  {p.room_title}
                  {!inRoom && p.rooms > 1 ? <span className="muted">{` +${p.rooms - 1}`}</span> : null}
                </div>
                {p.source ? <div className="xs muted">{`via ${p.source}`}</div> : null}
              </td>
              <td>
                <StagePill p={p} />
              </td>
              <td>
                {p.pressed_play ? (
                  <div className="depth">
                    <div className="bar">
                      <i style={{ width: `${p.watched_pct}%` }} />
                    </div>
                    <b className="small">{`${p.watched_pct}%`}</b>
                  </div>
                ) : (
                  <span className="muted small">Hasn't watched</span>
                )}
              </td>
              <td>
                <div className="row g4 wrap">
                  {p.tags.map((t) => (
                    <span key={t} className="tag">
                      {t}
                    </span>
                  ))}
                </div>
              </td>
              <td className="muted nowrap">{lastLine(p)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

